import React from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Edit2, Package, ExternalLink } from "lucide-react";

const empty = () => ({
    name: "", contact_name: "", phone: "", email: "", address: "", website: "", notes: ""
});

export default function AdminSuppliers() {
    const [items, setItems] = React.useState([]);
    const [editing, setEditing] = React.useState(null);
    const [viewProducts, setViewProducts] = React.useState(null); // { supplier, products[] }
    const [saving, setSaving] = React.useState(false);

    const load = React.useCallback(() => {
        api.get("/admin/suppliers").then(r => setItems(r.data || [])).catch(() => {});
    }, []);

    React.useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!editing.name.trim()) return toast.error("Numele furnizorului este obligatoriu");
        setSaving(true);
        try {
            if (editing.supplier_id) {
                await api.put(`/admin/suppliers/${editing.supplier_id}`, editing);
            } else {
                await api.post("/admin/suppliers", editing);
            }
            toast.success("Salvat"); setEditing(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
        setSaving(false);
    };

    const del = async (id) => {
        if (!window.confirm("Ștergi furnizorul?")) return;
        await api.delete(`/admin/suppliers/${id}`);
        load();
    };

    const openProducts = async (s) => {
        const r = await api.get(`/admin/suppliers/${s.supplier_id}/products`);
        setViewProducts({ supplier: s, products: r.data || [] });
    };

    return (
        <div data-testid="admin-suppliers">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="overline mb-1">Administrare</div>
                    <h1 className="font-heading text-3xl">Furnizori</h1>
                </div>
                <button onClick={() => setEditing(empty())} className="btn-primary flex items-center gap-2">
                    <Plus className="h-4 w-4" /> Furnizor nou
                </button>
            </div>

            {items.length === 0 ? (
                <div className="card-soft p-12 text-center text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Niciun furnizor adăugat.</p>
                    <button onClick={() => setEditing(empty())} className="btn-primary mt-4">Adaugă primul furnizor</button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map(s => (
                        <div key={s.supplier_id} className="card-soft p-4 flex flex-col gap-3">
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <div className="font-semibold text-base">{s.name}</div>
                                    {s.contact_name && <div className="text-sm text-muted-foreground">{s.contact_name}</div>}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button onClick={() => setEditing({ ...s })} className="btn-ghost p-1.5"><Edit2 className="h-3.5 w-3.5" /></button>
                                    <button onClick={() => del(s.supplier_id)} className="btn-ghost p-1.5 text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                            </div>
                            <div className="text-sm space-y-1">
                                {s.phone && <div className="text-muted-foreground">📞 {s.phone}</div>}
                                {s.email && <div className="text-muted-foreground">✉ {s.email}</div>}
                                {s.address && <div className="text-muted-foreground">📍 {s.address}</div>}
                                {s.website && <a href={s.website} target="_blank" rel="noopener noreferrer" className="text-primary flex items-center gap-1 text-xs hover:underline"><ExternalLink className="h-3 w-3" />{s.website}</a>}
                            </div>
                            {s.notes && <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2">{s.notes}</div>}
                            <button onClick={() => openProducts(s)} className="btn-outline text-sm flex items-center gap-2 mt-auto">
                                <Package className="h-4 w-4" /> Produse asociate
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Edit modal */}
            {editing && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <h3 className="font-heading text-xl">{editing.supplier_id ? "Editează furnizor" : "Furnizor nou"}</h3>
                            <button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <input required placeholder="Nume furnizor *" value={editing.name}
                                onChange={e => setEditing(v => ({ ...v, name: e.target.value }))} className="input-soft" />
                            <input placeholder="Persoană contact" value={editing.contact_name}
                                onChange={e => setEditing(v => ({ ...v, contact_name: e.target.value }))} className="input-soft" />
                            <div className="grid grid-cols-2 gap-3">
                                <input placeholder="Telefon" value={editing.phone}
                                    onChange={e => setEditing(v => ({ ...v, phone: e.target.value }))} className="input-soft" />
                                <input placeholder="Email" value={editing.email}
                                    onChange={e => setEditing(v => ({ ...v, email: e.target.value }))} className="input-soft" />
                            </div>
                            <input placeholder="Adresă" value={editing.address}
                                onChange={e => setEditing(v => ({ ...v, address: e.target.value }))} className="input-soft" />
                            <input placeholder="Website (https://...)" value={editing.website}
                                onChange={e => setEditing(v => ({ ...v, website: e.target.value }))} className="input-soft" />
                            <textarea rows="3" placeholder="Note interne" value={editing.notes}
                                onChange={e => setEditing(v => ({ ...v, notes: e.target.value }))} className="input-soft" />
                        </div>
                        <div className="p-5 border-t border-border flex justify-end gap-2">
                            <button onClick={() => setEditing(null)} className="btn-outline">Anulează</button>
                            <button onClick={save} disabled={saving} className="btn-primary">{saving ? "..." : "Salvează"}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Products modal */}
            {viewProducts && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <h3 className="font-heading text-xl">Produse — {viewProducts.supplier.name}</h3>
                            <button onClick={() => setViewProducts(null)}><X className="h-5 w-5" /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5">
                            {viewProducts.products.length === 0 ? (
                                <p className="text-muted-foreground text-center py-8">
                                    Niciun produs asociat acestui furnizor.<br />
                                    <span className="text-sm">Setează câmpul "Furnizor" din editorul de produs.</span>
                                </p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead><tr className="border-b border-border text-xs text-muted-foreground uppercase">
                                        <th className="pb-2 text-left">Produs</th>
                                        <th className="pb-2 text-right">Stoc</th>
                                        <th className="pb-2 text-right">Preț vânzare</th>
                                        <th className="pb-2 text-right">Preț achiziție</th>
                                        <th className="pb-2 text-right">Marjă</th>
                                    </tr></thead>
                                    <tbody>{viewProducts.products.map(p => {
                                        const sell = Number(p.price || 0);
                                        const buy = Number(p.purchase_price || 0);
                                        const margin = buy > 0 && sell > 0 ? ((sell - buy) / sell * 100).toFixed(1) : null;
                                        return (
                                            <tr key={p.product_id} className="border-b border-border/40 last:border-0">
                                                <td className="py-2 pr-2">
                                                    <div className="font-medium truncate max-w-[180px]">{p.name}</div>
                                                    <div className="text-xs text-muted-foreground font-mono">{p.code}</div>
                                                </td>
                                                <td className="py-2 text-right">{p.stock ?? "—"}</td>
                                                <td className="py-2 text-right">{sell.toFixed(2)} RON</td>
                                                <td className="py-2 text-right">{buy > 0 ? `${buy.toFixed(2)} RON` : "—"}</td>
                                                <td className="py-2 text-right font-medium text-emerald-600">{margin ? `${margin}%` : "—"}</td>
                                            </tr>
                                        );
                                    })}</tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
